import { createContext, useState, useContext, useCallback, ReactNode, useEffect } from 'react';
import { CaseCreateModal } from '../CaseCreateModal';

interface CasesContextProps {
  isCreateOpen: boolean;
  toggleModal: (clientId?: string) => void;
}

const CasesContext = createContext<CasesContextProps | undefined>(undefined);

export const CasesProvider = ({ children }: { children: ReactNode }) => {
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [clientId, setClientId] = useState('');

  const toggleModal = useCallback((clientId?: string) => {
    setCreateOpen((prev) => !prev);
    setClientId(clientId ?? '');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey && event.code === 'Space') || (event.ctrlKey && event.code === 'Space')) {
        event.preventDefault();
        toggleModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleModal]);

  return (
    <CasesContext.Provider value={{ isCreateOpen, toggleModal }}>
      {children}
      <CaseCreateModal clientId={clientId} isOpen={isCreateOpen} onClose={() => toggleModal()} />
    </CasesContext.Provider>
  );
};

export const useCases = (): CasesContextProps => {
  const context = useContext(CasesContext);
  if (!context) {
    throw new Error('useCases must be used within a CasesProvider');
  }
  return context;
};
